/**
 * `heysegment` CLI entry point.
 *
 * Wires the root command to its subcommands, runs it against the Node-provided
 * environment (FileSystem, Path, Stdio, Terminal, ChildProcessSpawner), and lets
 * `NodeRuntime.runMain` map the result to a process exit code via the runtime
 * markers carried on `OperationFailed` / `UsageError` (see `errors.ts`).
 *
 * Node (not bun) is the published runtime so the CLI runs in any user's
 * environment. The bin shebang is added to the built bundle by tsup; in-repo dev
 * runs `bun src/cli.ts` (bun executes the node-targeted code fine).
 *
 * @module
 */
import { NodeRuntime, NodeServices } from "@effect/platform-node"
import { Console, Effect } from "effect"
import { Command } from "effect/unstable/cli"
import { version as VERSION } from "../package.json"
import { validateCommand } from "./commands/validate"
import { sessionCommand } from "./commands/session"
import { pullCommand } from "./commands/pull"
import { pushCommand, saveCommand } from "./commands/push"
import { checkCommand } from "./commands/check"
import { statusCommand } from "./commands/status"
import { switchCommand } from "./commands/switch"
import { acknowledgeCommand } from "./commands/acknowledge"
import { reportCommand } from "./commands/report"
import { codegenCommand } from "./commands/codegen"
import { recallCommand } from "./commands/recall"
import { imagesCommand } from "./commands/images"
import { videosCommand } from "./commands/videos"
import { mediaCommand } from "./commands/media"
import { doctorCommand } from "./commands/doctor"

const isUsageError = (error: unknown): error is { readonly _tag: "UsageError"; readonly reason: string } =>
  typeof error === "object" &&
  error !== null &&
  "_tag" in error &&
  "reason" in error &&
  error._tag === "UsageError" &&
  typeof error.reason === "string"

const heysegment = Command.make("heysegment").pipe(
  Command.withDescription("Validate, build, and package heysegment projects"),
  Command.withSubcommands([
    validateCommand,
    sessionCommand,
    pullCommand,
    pushCommand,
    saveCommand,
    checkCommand,
    statusCommand,
    switchCommand,
    acknowledgeCommand,
    reportCommand,
    codegenCommand,
    recallCommand,
    imagesCommand,
    videosCommand,
    mediaCommand,
    doctorCommand,
  ]),
)

const program = Command.run(heysegment, { version: VERSION }).pipe(
  // UsageError is suppressed from runMain's logger (errorReported=false), so
  // surface its message ourselves. OperationFailed is already fully presented
  // by `emitReports`, and unexpected defects still fall through to runMain.
  Effect.tapError((error) =>
    isUsageError(error) ? Console.error(`error: ${error.reason}`) : Effect.void,
  ),
  Effect.provide(NodeServices.layer),
)

NodeRuntime.runMain(program)
